import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Moon, Sun, Star, Play, Pause, SkipForward, Square, MoonStar } from "lucide-react";
import { fetchSongs, resolveImage } from "@/lib/api";

const Rest = () => {
  const [night, setNight] = useState(false);
  const [songs, setSongs] = useState([]);
  const [queue, setQueue] = useState(null); // { list, idx }
  const [paused, setPaused] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchSongs("Bedtime").then(setSongs).catch(() => {});
    return () => audioRef.current?.pause();
  }, []);

  const playable = useMemo(() => songs.filter((s) => s.audio_url), [songs]);

  const playAt = (list, idx) => {
    audioRef.current?.pause();
    const a = new Audio(resolveImage(list[idx].audio_url));
    audioRef.current = a;
    a.onended = () => {
      if (idx + 1 < list.length) playAt(list, idx + 1);
      else setQueue(null);
    };
    a.play().catch(() => {});
    setQueue({ list, idx });
    setPaused(false);
  };

  const startPlaylist = () => playable.length && playAt(playable, 0);
  const stopQueue = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setQueue(null);
    setPaused(false);
  };
  const togglePause = () => {
    if (!audioRef.current) return;
    if (paused) {
      audioRef.current.play().catch(() => {});
      setPaused(false);
    } else {
      audioRef.current.pause();
      setPaused(true);
    }
  };
  const nextTrack = () => {
    if (!queue) return;
    if (queue.idx + 1 < queue.list.length) playAt(queue.list, queue.idx + 1);
    else stopQueue();
  };

  const stars = useMemo(
    () =>
      Array.from({ length: 26 }, (_, i) => ({
        left: `${(i * 37) % 100}%`,
        top: `${(i * 53) % 100}%`,
        delay: `${(i % 7) * 0.4}s`,
        size: 3 + (i % 3) * 2,
      })),
    []
  );

  const current = queue?.list[queue.idx];

  return (
    <motion.section
      animate={{ backgroundColor: night ? "#0F172A" : "#FFFDF6" }}
      transition={{ duration: 0.8 }}
      id="rest"
      className="relative flex min-h-[100svh] flex-col justify-center overflow-hidden py-28 sm:py-36"
      data-testid="rest-section"
    >
      {night &&
        stars.map((s, i) => (
          <span
            key={i}
            className="animate-twinkle pointer-events-none absolute rounded-full bg-[#FEF3C7]"
            style={{ left: s.left, top: s.top, width: s.size, height: s.size, animationDelay: s.delay }}
            aria-hidden="true"
          />
        ))}
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className={`text-xs font-bold uppercase tracking-[0.3em] ${night ? "text-[#C084FC]" : "text-[#9333EA]"}`}>
              Rest
            </p>
            <h2
              className={`mt-3 max-w-lg font-display text-3xl font-bold tracking-tight sm:text-4xl transition-colors duration-500 ${
                night ? "text-[#FEF3C7]" : "text-slate-900"
              }`}
            >
              When the day is done, peace takes over.
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-3 self-start md:self-auto">
            <button
              onClick={startPlaylist}
              disabled={!playable.length}
              data-testid="sleep-playlist-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#9333EA] px-6 py-3 font-display text-base font-semibold text-white shadow-[0_10px_30px_-5px_rgba(147,51,234,0.45)] transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
            >
              <MoonStar className="h-5 w-5" />
              Play sleep playlist
            </button>
            <button
              onClick={() => setNight((n) => !n)}
              data-testid="night-mode-toggle"
              className={`inline-flex items-center gap-3 rounded-full border-2 px-6 py-3 font-display text-base font-semibold transition-all hover:scale-105 active:scale-95 ${
                night
                  ? "border-[#C084FC] bg-[#9333EA]/20 text-[#E9D5FF]"
                  : "border-slate-900 bg-white text-slate-900"
              }`}
            >
              {night ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              {night ? "Morning mode" : "Night sky mode"}
            </button>
          </div>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-2">
          {songs.map((l, i) => {
            const isCurrent = current?.song_id === l.song_id;
            return (
              <motion.div
                key={l.song_id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                whileHover={{ y: -6 }}
                className={`rounded-3xl border p-6 backdrop-blur transition-colors duration-500 ${
                  night
                    ? "border-white/10 bg-white/5 text-[#FEF3C7]"
                    : "border-[#E2DDD0] bg-white text-slate-900 shadow-[0_10px_30px_-5px_rgba(147,51,234,0.12)]"
                } ${isCurrent ? (night ? "ring-2 ring-[#C084FC]" : "ring-2 ring-[#9333EA]") : ""}`}
                data-testid={`lullaby-card-${i}`}
              >
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => {
                      const idx = playable.findIndex((s) => s.song_id === l.song_id);
                      if (isCurrent) togglePause();
                      else if (idx >= 0) playAt(playable, idx);
                    }}
                    data-testid={`lullaby-play-${l.song_id}`}
                    aria-label={isCurrent && !paused ? `Pause ${l.title}` : `Play ${l.title}`}
                    className={`grid h-11 w-11 place-items-center rounded-full transition-transform hover:scale-110 active:scale-95 ${
                      night ? "bg-[#9333EA] text-white" : "bg-[#F3E8FF] text-[#9333EA]"
                    }`}
                  >
                    {isCurrent && !paused ? (
                      <Pause className="h-4 w-4 fill-current" />
                    ) : (
                      <Play className="ml-0.5 h-4 w-4 fill-current" />
                    )}
                  </button>
                  <Star className={`h-4 w-4 ${night ? "fill-[#FEF3C7] text-[#FEF3C7]" : "fill-[#FCD34D] text-[#FCD34D]"}`} />
                </div>
                <h3 className="mt-4 font-display text-lg font-bold leading-snug">{l.title}</h3>
                <p className={`mt-1 text-sm ${night ? "text-[#C4B5FD]" : "text-slate-500"}`}>{l.verse}</p>
                <p className={`mt-3 text-xs font-bold ${night ? "text-[#A78BFA]" : "text-slate-400"}`}>
                  {l.duration}
                  {isCurrent ? " — playing now" : ""}
                </p>
              </motion.div>
            );
          })}
        </div>

        <p
          className={`mt-10 text-center font-hand text-2xl transition-colors duration-500 ${
            night ? "text-[#C4B5FD]" : "text-slate-400"
          }`}
        >
          &ldquo;In peace I will lie down and sleep, for you alone, Lord, make me dwell in
          safety.&rdquo; — Psalm 4:8
        </p>
      </div>

      <AnimatePresence>
        {queue && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-full border border-white/10 bg-slate-900/95 py-2.5 pl-4 pr-3 text-white shadow-2xl backdrop-blur"
            data-testid="sleep-player"
          >
            <span className="grid h-9 w-9 place-items-center rounded-full bg-[#9333EA]">
              <MoonStar className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="max-w-44 truncate font-display text-sm font-semibold" data-testid="sleep-now-title">
                {current?.title}
              </p>
              <p className="text-xs text-slate-400">
                Lullaby {queue.idx + 1} of {queue.list.length} — sleep playlist
              </p>
            </div>
            <button
              onClick={togglePause}
              data-testid="sleep-pause-btn"
              aria-label={paused ? "Resume playlist" : "Pause playlist"}
              className="grid h-10 w-10 place-items-center rounded-full bg-white/10 transition-colors hover:bg-white/20"
            >
              {paused ? <Play className="ml-0.5 h-4 w-4 fill-white" /> : <Pause className="h-4 w-4 fill-white" />}
            </button>
            <button
              onClick={nextTrack}
              data-testid="sleep-next-btn"
              aria-label="Next lullaby"
              className="grid h-10 w-10 place-items-center rounded-full bg-white/10 transition-colors hover:bg-white/20"
            >
              <SkipForward className="h-4 w-4" />
            </button>
            <button
              onClick={stopQueue}
              data-testid="sleep-stop-btn"
              aria-label="Stop playlist"
              className="grid h-10 w-10 place-items-center rounded-full bg-white/10 transition-colors hover:bg-[#FF6B6B]"
            >
              <Square className="h-3.5 w-3.5 fill-current" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
};

export default Rest;
